import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from 'src/Lca/components/Breadcrumbs';
import LcaLayout from 'src/Lca/components/layout';
import {
  getHelpCentreContent,
  getLcaVideoInstructionsUrl,
  GuideSection,
  HelpCentreContent,
  HelpTab,
} from './content';
import tokenize from './search';

const matchesQuery = (parts: string[], query: string) =>
  !query || parts.join(' ').toLowerCase().includes(query);

const stripStepNumber = (title: string) => title.replace(/^\d+\.\s+/, '');

type SearchSuggestion = {
  id: string;
  source: string;
  summary: string;
  tab: HelpTab;
  title: string;
};

const diceCoefficient = (left: string, right: string) => {
  if (!left || !right) {
    return 0;
  }

  if (left === right) {
    return 1;
  }

  if (left.length < 2 || right.length < 2) {
    return 0;
  }

  const leftBigrams = new Map<string, number>();
  for (let index = 0; index < left.length - 1; index += 1) {
    const bigram = left.slice(index, index + 2);
    leftBigrams.set(bigram, (leftBigrams.get(bigram) || 0) + 1);
  }

  let intersection = 0;
  for (let index = 0; index < right.length - 1; index += 1) {
    const bigram = right.slice(index, index + 2);
    const count = leftBigrams.get(bigram) || 0;
    if (count > 0) {
      leftBigrams.set(bigram, count - 1);
      intersection += 1;
    }
  }

  return (2 * intersection) / (left.length + right.length - 2);
};

const getSuggestionScore = (
  query: string,
  suggestion: SearchSuggestion,
  activeTab: HelpTab
) => {
  const normalizedTitle = suggestion.title.toLowerCase();
  const normalizedSummary = suggestion.summary.toLowerCase();
  const queryTokens = tokenize(query);
  const candidateTokens = new Set(
    tokenize(`${suggestion.title} ${suggestion.summary} ${suggestion.source}`)
  );

  const commonTokens = queryTokens.filter((token) =>
    candidateTokens.has(token)
  );
  const overlapScore = queryTokens.length
    ? commonTokens.length / queryTokens.length
    : 0;
  const containsScore =
    normalizedTitle.includes(query) || normalizedSummary.includes(query)
      ? 1
      : 0;
  const prefixScore = tokenize(suggestion.title).some(
    (token) => token.startsWith(query) || query.startsWith(token)
  )
    ? 1
    : 0;
  const similarityScore = Math.max(
    diceCoefficient(query, normalizedTitle),
    diceCoefficient(query, normalizedSummary)
  );
  const tabScore = suggestion.tab === activeTab ? 0.2 : 0;

  return (
    overlapScore * 4 +
    containsScore * 3 +
    prefixScore * 1.5 +
    similarityScore * 3 +
    tabScore
  );
};

const buildSearchSuggestions = (
  content: HelpCentreContent,
  query: string,
  activeTab: HelpTab
) => {
  const suggestions: SearchSuggestion[] = [
    ...content.knowledgeCards.map((card) => ({
      id: `knowledge-${card.tab}-${card.title}`,
      source: card.status,
      summary: card.summary,
      tab: card.tab,
      title: card.title,
    })),
    ...content.workflowSteps.map((step) => ({
      id: `workflow-${step.tab}-${step.title}`,
      source: step.owner,
      summary: step.summary,
      tab: step.tab,
      title: step.title,
    })),
    ...content.guideSections.flatMap((section) =>
      section.entries.map((entry) => ({
        id: `guide-${section.tab}-${section.title}-${entry.title}`,
        source: section.title,
        summary: entry.description,
        tab: section.tab,
        title: entry.title,
      }))
    ),
    ...content.faqs
      .filter((faq) => faq.tab !== 'all')
      .map((faq) => ({
        id: `faq-${faq.tab}-${faq.question}`,
        source: content.faqTitle,
        summary: faq.answer,
        tab: faq.tab as HelpTab,
        title: faq.question,
      })),
  ];

  return suggestions
    .map((suggestion) => ({
      ...suggestion,
      score: getSuggestionScore(query, suggestion, activeTab),
    }))
    .filter((suggestion) => suggestion.score > 0.45)
    .sort((left, right) => right.score - left.score)
    .slice(0, 5);
};

const quickActionIcons: Record<HelpTab, React.ReactElement> = {
  platform: <PlayCircleOutlineOutlinedIcon />,
  lca: <TimelineOutlinedIcon />,
  ecoinvent: <FactCheckOutlinedIcon />,
  dpp: <HubOutlinedIcon />,
  regulations: <GavelOutlinedIcon />,
};

const tabIcons: Record<HelpTab, React.ReactElement> = {
  platform: <AutoStoriesOutlinedIcon />,
  lca: <TimelineOutlinedIcon />,
  ecoinvent: <FactCheckOutlinedIcon />,
  dpp: <HubOutlinedIcon />,
  regulations: <GavelOutlinedIcon />,
};

const flowchartImpactValues = [
  '3.4',
  '6.8',
  '10.2',
  '13.6',
  '17.0',
  '20.4',
  '23.8',
];

const getAnalyticsGuideVisualLabels = (language: string) => {
  const isPolish = language.toLowerCase().startsWith('pl');
  const isSwedish = language.toLowerCase().startsWith('sv');

  if (isPolish) {
    return {
      badge: 'Analytics guide',
      baseline: 'Baseline',
      confidence: 'Pasmo niepewności',
      lightweighting: 'Lightweighting',
      monteCarloDescription: 'Zmienność wokół bieżącego łącznego wyniku',
      monteCarloTitle: 'Monte Carlo',
      orderTitle: 'Rekomendowana kolejność',
      previewTitle: 'Wizualne streszczenie',
      recycled: 'Recycled content',
      scenarioDescription: 'Efekt what-if dla kg CO2-Eq',
      scenarioTitle: 'Scenario Explorer',
      scenarioUnit: 'kg CO2-Eq',
      stepOne: '1. Sprawdź impact',
      stepThree: '3. Zaktualizuj model',
      stepTwo: '2. Przetestuj scenariusze',
      transport: 'Transport',
      trend: 'Trend średniej',
    };
  }

  if (isSwedish) {
    return {
      badge: 'Analysguide',
      baseline: 'Baslinje',
      confidence: 'Osäkerhetsintervall',
      lightweighting: 'Lättviktning',
      monteCarloDescription: 'Variation kring det aktuella totalresultatet',
      monteCarloTitle: 'Monte Carlo',
      orderTitle: 'Rekommenderad ordning',
      previewTitle: 'Visuell sammanfattning',
      recycled: 'Återvunnet innehåll',
      scenarioDescription: 'Scenarioeffekt för kg CO2-Eq',
      scenarioTitle: 'Scenarioutforskare',
      scenarioUnit: 'kg CO2-Eq',
      stepOne: '1. Kontrollera påverkan',
      stepThree: '3. Uppdatera modellen',
      stepTwo: '2. Testa scenarier',
      transport: 'Transport',
      trend: 'Medeltrend',
    };
  }

  return {
    badge: 'Analytics guide',
    baseline: 'Baseline',
    confidence: 'Confidence band',
    lightweighting: 'Lightweighting',
    monteCarloDescription: 'Variability around the current total result',
    monteCarloTitle: 'Monte Carlo',
    orderTitle: 'Recommended order',
    previewTitle: 'Visual summary',
    recycled: 'Recycled content',
    scenarioDescription: 'What-if effect on kg CO2-Eq',
    scenarioTitle: 'Scenario Explorer',
    scenarioUnit: 'kg CO2-Eq',
    stepOne: '1. Check impact',
    stepThree: '3. Update model',
    stepTwo: '2. Test scenarios',
    transport: 'Transport',
    trend: 'Mean trend',
  };
};

const getPassportGuideVisualLabels = (language: string) => {
  const isPolish = language.toLowerCase().startsWith('pl');
  const isSwedish = language.toLowerCase().startsWith('sv');

  if (isPolish) {
    return {
      badge: 'Ekran platformy',
      builderTitle: 'Passport builder',
      previewTitle: 'Publiczny preview',
      productName: 'Acoustic Panel X12',
      share: 'Udostępnij',
      sectionLabel: 'Widoczne bloki',
      sections: [
        'Product identification',
        'Environmental footprint',
        'Material composition',
        'Durability & repair',
        'Compliance & certifications',
      ],
      passportTitle: 'Product passport',
      passportSubtitle: 'Widok gotowy do DPP',
      facts: ['ESPR ready', 'QR enabled', '3 certifications'],
      qrLabel: 'Link publiczny + QR',
      caption: 'Widok buildera po lewej i publicznego preview po prawej.',
    };
  }

  if (isSwedish) {
    return {
      badge: 'Plattformsvy',
      builderTitle: 'Passbyggare',
      previewTitle: 'Offentlig förhandsvisning',
      productName: 'Acoustic Panel X12',
      share: 'Dela',
      sectionLabel: 'Synliga block',
      sections: [
        'Produktidentifiering',
        'Miljöavtryck',
        'Materialsammansättning',
        'Hållbarhet och reparation',
        'Efterlevnad och certifieringar',
      ],
      passportTitle: 'Produktpass',
      passportSubtitle: 'DPP-klar vy',
      facts: ['ESPR-klar', 'QR aktiverad', '3 certifieringar'],
      qrLabel: 'Offentlig länk + QR',
      caption:
        'Redigeringskontroller till vänster och offentlig passförhandsvisning till höger.',
    };
  }

  return {
    badge: 'Platform screen',
    builderTitle: 'Passport builder',
    previewTitle: 'Public preview',
    productName: 'Acoustic Panel X12',
    share: 'Share',
    sectionLabel: 'Visible blocks',
    sections: [
      'Product identification',
      'Environmental footprint',
      'Material composition',
      'Durability & repair',
      'Compliance & certifications',
    ],
    passportTitle: 'Product passport',
    passportSubtitle: 'DPP-ready view',
    facts: ['ESPR ready', 'QR enabled', '3 certifications'],
    qrLabel: 'Public link + QR',
    caption:
      'Builder controls on the left and public passport preview on the right.',
  };
};

const getReportGuideVisualLabels = (language: string) => {
  const isPolish = language.toLowerCase().startsWith('pl');
  const isSwedish = language.toLowerCase().startsWith('sv');

  if (isPolish) {
    return {
      badge: 'Ekran platformy',
      builderTitle: 'Report builder',
      export: 'Eksport PDF',
      productName: 'Acoustic Panel X12',
      steps: ['Zakres', 'Narracja', 'Eksport'],
      sectionsTitle: 'Sekcje raportu',
      sections: [
        'Executive summary',
        'Goal and scope',
        'Inventory & datasets',
        'Results & interpretation',
        'Annexes',
      ],
      previewTitle: 'Preview raportu',
      previewSubtitle: 'LCA Report / EPD / PEF / Scope 3 input',
      chips: ['Reviewed', 'Audit trail', 'Versioned'],
      caption:
        'Po lewej konfiguracja sekcji, po prawej podgląd eksportowanego raportu.',
    };
  }

  if (isSwedish) {
    return {
      badge: 'Plattformsvy',
      builderTitle: 'Rapportbyggare',
      export: 'Exportera PDF',
      productName: 'Acoustic Panel X12',
      steps: ['Omfattning', 'Narrativ', 'Export'],
      sectionsTitle: 'Rapportsektioner',
      sections: [
        'Sammanfattning',
        'Mål och omfattning',
        'Inventering och dataset',
        'Resultat och tolkning',
        'Bilagor',
      ],
      previewTitle: 'Rapportförhandsvisning',
      previewSubtitle: 'LCA-rapport / EPD / PEF / Scope 3-underlag',
      chips: ['Granskad', 'Revisionsspår', 'Versionshanterad'],
      caption:
        'Till vänster konfiguration av sektioner, till höger förhandsvisning av den exporterade rapporten.',
    };
  }

  return {
    badge: 'Platform screen',
    builderTitle: 'Report builder',
    export: 'Export PDF',
    productName: 'Acoustic Panel X12',
    steps: ['Scope', 'Narrative', 'Export'],
    sectionsTitle: 'Report sections',
    sections: [
      'Executive summary',
      'Goal and scope',
      'Inventory & datasets',
      'Results & interpretation',
      'Annexes',
    ],
    previewTitle: 'Report preview',
    previewSubtitle: 'LCA Report / EPD / PEF / Scope 3 input',
    chips: ['Reviewed', 'Audit trail', 'Versioned'],
    caption:
      'Section configuration on the left and exported report preview on the right.',
  };
};

function AnalyticsGuideVisual({ language }: { language: string }) {
  const theme = useTheme();
  const labels = getAnalyticsGuideVisualLabels(language);

  const scenarioBars = [
    {
      color: theme.palette.mainDark,
      label: labels.baseline,
      value: '42.4',
      width: '100%',
    },
    {
      color: '#4f8f66',
      label: labels.recycled,
      value: '35.1',
      width: '83%',
    },
    {
      color: '#bf7b3d',
      label: labels.lightweighting,
      value: '31.6',
      width: '75%',
    },
    {
      color: '#5b87c5',
      label: labels.transport,
      value: '37.0',
      width: '87%',
    },
  ];

  return (
    <Stack
      spacing={2}
      sx={{
        position: { lg: 'sticky' },
        top: { lg: 24 },
      }}
    >
      <Card
        sx={{
          borderRadius: '24px',
          border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
          boxShadow: 'none',
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.mainLight,
            0.12
          )} 0%, ${theme.palette.common.white} 100%)`,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Chip
            size="small"
            label={labels.badge}
            sx={{
              mb: 1.5,
              bgcolor: alpha(theme.palette.mainDark, 0.08),
              color: theme.palette.mainDark,
            }}
          />
          <Typography variant="h5" sx={{ mb: 0.75 }}>
            {labels.previewTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.scenarioDescription}
          </Typography>

          <Box
            sx={{
              borderRadius: '20px',
              p: 2,
              border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
              backgroundColor: alpha(theme.palette.common.white, 0.86),
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {labels.scenarioTitle}
            </Typography>
            <Stack spacing={1.25}>
              {scenarioBars.map((bar) => (
                <Box key={bar.label}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {bar.label}
                    </Typography>
                    <Typography variant="caption" fontWeight={700}>
                      {`${bar.value} ${labels.scenarioUnit}`}
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      height: 10,
                      borderRadius: '999px',
                      backgroundColor: alpha(theme.palette.mainDark, 0.08),
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: bar.width,
                        height: '100%',
                        borderRadius: '999px',
                        backgroundColor: bar.color,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              mt: 2,
              borderRadius: '20px',
              p: 2,
              border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
              backgroundColor: alpha(theme.palette.common.white, 0.86),
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              {labels.monteCarloTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {labels.monteCarloDescription}
            </Typography>
            <Box
              component="svg"
              viewBox="0 0 280 140"
              sx={{ width: '100%', height: 'auto', mt: 1.5 }}
            >
              <path
                d="M24 94 C58 56, 94 52, 126 78 S192 116, 256 62"
                fill="none"
                stroke={alpha('#4f8f66', 0.18)}
                strokeLinecap="round"
                strokeWidth="34"
              />
              <path
                d="M24 96 C58 66, 94 62, 126 78 S192 102, 256 70"
                fill="none"
                stroke={theme.palette.mainDark}
                strokeLinecap="round"
                strokeWidth="4"
              />
              <line
                x1="24"
                x2="256"
                y1="118"
                y2="118"
                stroke={alpha(theme.palette.mainDark, 0.14)}
                strokeWidth="2"
              />
              <circle cx="126" cy="78" fill={theme.palette.mainDark} r="5" />
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 0.5, flexWrap: 'wrap' }}
            >
              <Chip
                size="small"
                label={labels.trend}
                sx={{
                  bgcolor: alpha(theme.palette.mainDark, 0.08),
                  color: theme.palette.mainDark,
                }}
              />
              <Chip
                size="small"
                label={labels.confidence}
                sx={{
                  bgcolor: alpha('#4f8f66', 0.14),
                  color: '#2d5d3f',
                }}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              mt: 2,
              borderRadius: '20px',
              p: 2,
              border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
              backgroundColor: alpha(theme.palette.mainDark, 0.03),
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
              {labels.orderTitle}
            </Typography>
            <Stack spacing={1}>
              {[labels.stepOne, labels.stepTwo, labels.stepThree].map(
                (step) => (
                  <Box
                    key={step}
                    sx={{
                      px: 1.5,
                      py: 1.1,
                      borderRadius: '14px',
                      border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                      backgroundColor: theme.palette.common.white,
                    }}
                  >
                    <Typography variant="body2">{step}</Typography>
                  </Box>
                )
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}

function PassportGuideVisual({ language }: { language: string }) {
  const theme = useTheme();
  const labels = getPassportGuideVisualLabels(language);

  return (
    <Stack
      spacing={2}
      sx={{
        position: { lg: 'sticky' },
        top: { lg: 24 },
      }}
    >
      <Card
        sx={{
          borderRadius: '24px',
          border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
          boxShadow: 'none',
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.mainLight,
            0.12
          )} 0%, ${theme.palette.common.white} 100%)`,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Chip
            size="small"
            label={labels.badge}
            sx={{
              mb: 1.5,
              bgcolor: alpha(theme.palette.mainDark, 0.08),
              color: theme.palette.mainDark,
            }}
          />
          <Typography variant="h5" sx={{ mb: 0.75 }}>
            {labels.builderTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.caption}
          </Typography>

          <Box
            sx={{
              borderRadius: '22px',
              border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
              backgroundColor: theme.palette.common.white,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                backgroundColor: alpha(theme.palette.mainDark, 0.03),
              }}
            >
              <Box>
                <Typography variant="subtitle2">
                  {labels.productName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {labels.builderTitle}
                </Typography>
              </Box>
              <Chip
                icon={<LaunchOutlinedIcon />}
                label={labels.share}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.main, 0.12),
                  color: theme.palette.mainDark,
                }}
              />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1.5,
                p: 1.75,
              }}
            >
              <Box
                sx={{
                  borderRadius: '18px',
                  border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                  p: 1.5,
                  backgroundColor: alpha(theme.palette.mainLight, 0.05),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <HubOutlinedIcon
                    sx={{ color: theme.palette.mainDark, fontSize: 18 }}
                  />
                  <Typography variant="subtitle2">
                    {labels.sectionLabel}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {labels.sections.map((section) => (
                    <Box
                      key={section}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.25,
                        py: 1,
                        borderRadius: '14px',
                        backgroundColor: theme.palette.common.white,
                        border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ pr: 1 }}>
                        {section}
                      </Typography>
                      <TaskAltOutlinedIcon
                        sx={{ color: theme.palette.main, fontSize: 18 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'stretch',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    borderRadius: '28px',
                    border: `1px solid ${alpha(theme.palette.mainDark, 0.12)}`,
                    background:
                      'linear-gradient(180deg, #f7faf6 0%, #ffffff 100%)',
                    p: 1.5,
                    boxShadow: `0 16px 40px ${alpha(
                      theme.palette.mainDark,
                      0.08
                    )}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {labels.previewTitle}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 0.75 }}>
                    {labels.passportTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {labels.passportSubtitle}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}
                  >
                    {labels.facts.map((fact) => (
                      <Chip
                        key={fact}
                        size="small"
                        label={fact}
                        sx={{
                          bgcolor: alpha(theme.palette.mainDark, 0.08),
                          color: theme.palette.mainDark,
                        }}
                      />
                    ))}
                  </Stack>
                  <Box
                    sx={{
                      mt: 1.5,
                      borderRadius: '18px',
                      border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                      p: 1.25,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {labels.qrLabel}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        width: 74,
                        height: 74,
                        borderRadius: '12px',
                        background:
                          'linear-gradient(135deg, #12372f 0%, #4f8f66 100%)',
                        position: 'relative',
                      }}
                    >
                      {[
                        { left: 10, top: 10 },
                        { left: 44, top: 10 },
                        { left: 10, top: 44 },
                        { left: 31, top: 27 },
                        { left: 50, top: 48 },
                      ].map((dot) => (
                        <Box
                          key={`${dot.left}-${dot.top}`}
                          sx={{
                            position: 'absolute',
                            left: dot.left,
                            top: dot.top,
                            width: 12,
                            height: 12,
                            borderRadius: '4px',
                            backgroundColor: theme.palette.common.white,
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}

function ReportGuideVisual({ language }: { language: string }) {
  const theme = useTheme();
  const labels = getReportGuideVisualLabels(language);

  return (
    <Stack
      spacing={2}
      sx={{
        position: { lg: 'sticky' },
        top: { lg: 24 },
      }}
    >
      <Card
        sx={{
          borderRadius: '24px',
          border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
          boxShadow: 'none',
          background: `linear-gradient(180deg, ${alpha(
            theme.palette.mainLight,
            0.12
          )} 0%, ${theme.palette.common.white} 100%)`,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Chip
            size="small"
            label={labels.badge}
            sx={{
              mb: 1.5,
              bgcolor: alpha(theme.palette.mainDark, 0.08),
              color: theme.palette.mainDark,
            }}
          />
          <Typography variant="h5" sx={{ mb: 0.75 }}>
            {labels.builderTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {labels.caption}
          </Typography>

          <Box
            sx={{
              borderRadius: '22px',
              border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
              backgroundColor: theme.palette.common.white,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                backgroundColor: alpha(theme.palette.mainDark, 0.03),
              }}
            >
              <Box>
                <Typography variant="subtitle2">
                  {labels.productName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {labels.builderTitle}
                </Typography>
              </Box>
              <Chip
                icon={<LaunchOutlinedIcon />}
                label={labels.export}
                size="small"
                sx={{
                  bgcolor: alpha('#4f8f66', 0.14),
                  color: '#2d5d3f',
                }}
              />
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              sx={{ px: 1.5, py: 1.25, flexWrap: 'wrap' }}
            >
              {labels.steps.map((step, index) => (
                <Chip
                  key={step}
                  size="small"
                  icon={
                    index === 2 ? (
                      <TaskAltOutlinedIcon />
                    ) : (
                      <HelpOutlineOutlinedIcon />
                    )
                  }
                  label={step}
                  sx={{
                    bgcolor:
                      index === 2
                        ? alpha('#4f8f66', 0.14)
                        : alpha(theme.palette.mainDark, 0.08),
                    color: index === 2 ? '#2d5d3f' : theme.palette.mainDark,
                  }}
                />
              ))}
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1.5,
                p: 1.75,
              }}
            >
              <Box
                sx={{
                  borderRadius: '18px',
                  border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                  p: 1.5,
                  backgroundColor: alpha(theme.palette.mainLight, 0.05),
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                  {labels.sectionsTitle}
                </Typography>
                <Stack spacing={1}>
                  {labels.sections.map((section, index) => (
                    <Box
                      key={section}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.1,
                        py: 0.95,
                        borderRadius: '14px',
                        backgroundColor: theme.palette.common.white,
                        border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                      }}
                    >
                      {index < 3 ? (
                        <TaskAltOutlinedIcon
                          sx={{ color: theme.palette.main, fontSize: 18 }}
                        />
                      ) : (
                        <RadioButtonUncheckedOutlinedIcon
                          sx={{
                            color: alpha(theme.palette.mainDark, 0.4),
                            fontSize: 18,
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ minWidth: 0 }}>
                        {section}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Box
                sx={{
                  borderRadius: '18px',
                  border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                  p: 1.5,
                  backgroundColor: theme.palette.common.white,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {labels.previewTitle}
                </Typography>
                <Box
                  sx={{
                    mt: 1.25,
                    borderRadius: '18px',
                    border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                    background:
                      'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    p: 1.5,
                    minHeight: 240,
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {labels.previewSubtitle}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.75, mb: 1 }}>
                    {labels.productName}
                  </Typography>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: '999px',
                      bgcolor: alpha(theme.palette.mainDark, 0.08),
                      mb: 1.5,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: '72%',
                        height: '100%',
                        bgcolor: theme.palette.mainDark,
                      }}
                    />
                  </Box>
                  <Stack spacing={1}>
                    {[78, 94, 66, 88, 72].map((width, index) => (
                      <Box
                        key={String(width)}
                        sx={{
                          height: index === 0 ? 12 : 9,
                          borderRadius: '999px',
                          bgcolor: alpha(theme.palette.mainDark, 0.08),
                          width: `${width}%`,
                        }}
                      />
                    ))}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 2, flexWrap: 'wrap', rowGap: 0.75 }}
                  >
                    {labels.chips.map((chip) => (
                      <Chip
                        key={chip}
                        size="small"
                        label={chip}
                        sx={{
                          bgcolor: alpha(theme.palette.mainDark, 0.08),
                          color: theme.palette.mainDark,
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Stack>
  );
}

const filterGuideSections = (
  sections: GuideSection[],
  activeTab: HelpTab,
  query: string
) =>
  sections
    .filter((section) => section.tab === activeTab)
    .map((section) => {
      if (!query) {
        return section;
      }

      const sectionMatches = matchesQuery(
        [section.badge, section.title, section.description],
        query
      );
      const entries = sectionMatches
        ? section.entries
        : section.entries.filter((entry) =>
            matchesQuery(
              [entry.title, entry.description, ...entry.bullets],
              query
            )
          );

      return {
        ...section,
        entries,
      };
    })
    .filter((section) => section.entries.length > 0);

export default function HelpCentre() {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const videoInstructionsUrl = getLcaVideoInstructionsUrl(i18n.language);
  const content = getHelpCentreContent(i18n.language);

  const [activeTab, setActiveTab] = React.useState<HelpTab>('platform');
  const [query, setQuery] = React.useState('');
  const [completedItems, setCompletedItems] = React.useState<string[]>([]);

  const normalizedQuery = query.trim().toLowerCase();

  const visibleKnowledgeCards = content.knowledgeCards.filter(
    (card) =>
      card.tab === activeTab &&
      matchesQuery(
        [card.title, card.summary, card.status, ...card.bullets],
        normalizedQuery
      )
  );

  const visibleWorkflowSteps = content.workflowSteps.filter(
    (step) =>
      step.tab === activeTab &&
      matchesQuery(
        [step.title, step.summary, step.owner, ...step.outputs],
        normalizedQuery
      )
  );

  const visibleGuideSections = filterGuideSections(
    content.guideSections,
    activeTab,
    normalizedQuery
  );

  const visibleFaqs = content.faqs.filter(
    (faq) =>
      (faq.tab === 'all' || faq.tab === activeTab) &&
      matchesQuery([faq.question, faq.answer], normalizedQuery)
  );

  const flowchartMatches =
    activeTab === 'lca' &&
    matchesQuery(
      [
        content.flowchartTitle,
        content.flowchartDescription,
        content.flowchartExampleTitle,
        content.flowchartCanvasTitle,
        content.flowchartCanvasHint,
        content.flowchartLegendLabel,
        ...content.flowchartLegendItems,
        ...content.flowchartSteps.flatMap((step) => [
          step.title,
          step.description,
          ...step.bullets,
        ]),
        ...content.flowchartExampleStages.flatMap((stage) => [
          stage.title,
          ...stage.items,
        ]),
      ],
      normalizedQuery
    );

  const guideEntryCount = visibleGuideSections.reduce(
    (sum, section) => sum + section.entries.length,
    0
  );
  const matchingCount =
    visibleKnowledgeCards.length +
    visibleWorkflowSteps.length +
    visibleFaqs.length +
    guideEntryCount +
    (flowchartMatches ? 1 : 0);
  const showSearchFallback = Boolean(normalizedQuery) && matchingCount === 0;
  const searchSuggestions = showSearchFallback
    ? buildSearchSuggestions(content, normalizedQuery, activeTab)
    : [];

  const readiness = Math.round(
    (completedItems.length / content.checklistItems.length) * 100
  );

  const toggleChecklistItem = (itemId: string) => {
    setCompletedItems((current) =>
      current.includes(itemId)
        ? current.filter((entry) => entry !== itemId)
        : [...current, itemId]
    );
  };

  return (
    <LcaLayout>
      <Breadcrumbs data={content.breadcrumbs} />

      <Box sx={{ mt: 5, pb: 6 }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '32px',
            p: { xs: 3, md: 5 },
            background: `
              radial-gradient(circle at top right, ${alpha(theme.palette.mainLight, 0.32)} 0%, transparent 34%),
              radial-gradient(circle at bottom left, ${alpha(theme.palette.main, 0.16)} 0%, transparent 38%),
              linear-gradient(135deg, #ffffff 0%, #fff7eb 48%, #f5ede5 100%)
            `,
            border: `1px solid ${alpha(theme.palette.mainDark, 0.12)}`,
            boxShadow: `0 24px 60px ${alpha(theme.palette.mainDark, 0.14)}`,
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              justifyContent="space-between"
              spacing={3}
            >
              <Box sx={{ maxWidth: '860px' }}>
                <Chip
                  icon={<HelpOutlineOutlinedIcon />}
                  label={content.badge}
                  sx={{
                    mb: 2,
                    bgcolor: alpha(theme.palette.mainDark, 0.08),
                    color: theme.palette.mainDark,
                    fontWeight: 700,
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    maxWidth: '760px',
                    mb: 2,
                    color: theme.palette.mainDark,
                  }}
                >
                  {content.heroTitle}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    maxWidth: '760px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {content.heroDescription}
                </Typography>
              </Box>

              <Stack
                spacing={1.5}
                sx={{
                  minWidth: { xs: '100%', lg: '300px' },
                  alignSelf: 'stretch',
                  justifyContent: 'center',
                }}
              >
                {content.stats.map((stat) => (
                  <Box
                    key={stat}
                    sx={{
                      borderRadius: '20px',
                      px: 2.5,
                      py: 1.75,
                      bgcolor: alpha(theme.palette.common.white, 0.72),
                      border: `1px solid ${alpha(theme.palette.mainDark, 0.1)}`,
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ color: theme.palette.mainDark }}
                    >
                      {stat}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <TextField
                fullWidth
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={content.searchPlaceholder}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: '720px',
                  '& .MuiInputBase-root': {
                    bgcolor: alpha(theme.palette.common.white, 0.78),
                  },
                }}
              />

              <Button
                href={videoInstructionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<PlayCircleOutlineOutlinedIcon />}
              >
                {content.videoInstructionsLabel}
              </Button>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                {matchingCount} {content.searchResultsLabel}
              </Typography>
              <Chip
                label={content.disclaimer}
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  '& .MuiChip-label': {
                    display: 'block',
                    whiteSpace: 'normal',
                    py: 0.75,
                  },
                }}
              />
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            {content.quickActionsTitle}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            {content.quickActions.map((action) => {
              const active = action.id === activeTab;

              return (
                <Card
                  key={action.id}
                  onClick={() => setActiveTab(action.id)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: '24px',
                    border: `1px solid ${alpha(
                      theme.palette.mainDark,
                      active ? 0.22 : 0.08
                    )}`,
                    bgcolor: active
                      ? alpha(theme.palette.mainLight, 0.12)
                      : theme.palette.common.white,
                    transform: active ? 'translateY(-2px)' : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 16px 32px ${alpha(
                        theme.palette.mainDark,
                        0.12
                      )}`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 1.25,
                        mb: 2,
                        borderRadius: '16px',
                        bgcolor: alpha(theme.palette.mainDark, 0.08),
                        color: theme.palette.mainDark,
                      }}
                    >
                      {quickActionIcons[action.id]}
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ mt: 5 }}>
          <Tabs
            value={activeTab}
            onChange={(_, nextValue: HelpTab) => setActiveTab(nextValue)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.mainDark,
                height: 3,
                borderRadius: '999px',
              },
            }}
          >
            {(Object.keys(content.tabs) as HelpTab[]).map((tab) => (
              <Tab
                key={tab}
                value={tab}
                icon={tabIcons[tab]}
                iconPosition="start"
                label={content.tabs[tab]}
              />
            ))}
          </Tabs>

          {showSearchFallback ? (
            <Card
              sx={{
                mt: 1,
                borderRadius: '28px',
                border: `1px solid ${alpha(theme.palette.mainDark, 0.1)}`,
                boxShadow: 'none',
                background: `linear-gradient(180deg, ${alpha(
                  theme.palette.mainLight,
                  0.08
                )} 0%, ${theme.palette.common.white} 100%)`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {content.searchNoExactMatchTitle}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: searchSuggestions.length ? 2.5 : 0 }}
                >
                  {content.searchNoExactMatchDescription}
                </Typography>
                {searchSuggestions.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      {content.searchSuggestionsLabel}
                    </Typography>
                    <Stack spacing={1.25}>
                      {searchSuggestions.map((suggestion) => (
                        <Box
                          key={suggestion.id}
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            gap: 1.5,
                            p: 2,
                            borderRadius: '18px',
                            border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                            backgroundColor: theme.palette.common.white,
                          }}
                        >
                          <Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ mb: 0.75 }}
                            >
                              <Chip
                                size="small"
                                label={content.tabs[suggestion.tab]}
                                sx={{
                                  bgcolor: alpha(theme.palette.mainDark, 0.08),
                                  color: theme.palette.mainDark,
                                }}
                              />
                              <Chip
                                size="small"
                                label={suggestion.source}
                                sx={{
                                  bgcolor: alpha(theme.palette.main, 0.08),
                                  color: theme.palette.mainDark,
                                }}
                              />
                            </Stack>
                            <Typography variant="h6" sx={{ mb: 0.5 }}>
                              {suggestion.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {suggestion.summary}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setActiveTab(suggestion.tab);
                              setQuery(suggestion.title);
                            }}
                          >
                            {content.searchSuggestionOpenLabel}
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: {
                    xs: '1fr',
                    xl: 'repeat(2, minmax(0, 1fr))',
                  },
                }}
              >
                {visibleKnowledgeCards.map((card) => (
                  <Card
                    key={card.title}
                    sx={{
                      borderRadius: '24px',
                      border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Chip
                            size="small"
                            label={card.status}
                            sx={{
                              mb: 1.5,
                              bgcolor: alpha(theme.palette.mainDark, 0.08),
                              color: theme.palette.mainDark,
                            }}
                          />
                          <Typography variant="h4" sx={{ mb: 1 }}>
                            {card.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {card.summary}
                          </Typography>
                        </Box>
                        <FactCheckOutlinedIcon
                          sx={{ color: theme.palette.main }}
                        />
                      </Stack>

                      <Stack spacing={1.25}>
                        {card.bullets.map((bullet) => (
                          <Stack
                            key={bullet}
                            direction="row"
                            spacing={1.25}
                            alignItems="flex-start"
                          >
                            <VerifiedOutlinedIcon
                              sx={{
                                mt: '2px',
                                fontSize: '18px',
                                color: theme.palette.main,
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {bullet}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      {card.link && (
                        <Button
                          href={card.link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<LaunchOutlinedIcon />}
                          sx={{ mt: 2.5 }}
                        >
                          {card.link.label}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {!visibleKnowledgeCards.length && (
                <Card
                  sx={{
                    mt: 1,
                    borderRadius: '24px',
                    border: `1px dashed ${alpha(theme.palette.mainDark, 0.18)}`,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {content.noMatchingCardsTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {content.noMatchingCardsDescription}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Box>

        {(!showSearchFallback || visibleWorkflowSteps.length > 0) && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h3" sx={{ mb: 1.5 }}>
              {content.workflowTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {content.workflowDescription}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                  xl: 'repeat(3, minmax(0, 1fr))',
                },
              }}
            >
              {visibleWorkflowSteps.map((step, index) => (
                <Card
                  key={step.title}
                  sx={{
                    borderRadius: '24px',
                    border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                    boxShadow: 'none',
                    height: '100%',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Chip
                      size="small"
                      label={`${content.workflowOwnerLabel}: ${step.owner}`}
                      sx={{
                        mb: 2,
                        bgcolor: alpha(theme.palette.mainDark, 0.08),
                        color: theme.palette.mainDark,
                        height: 'auto',
                        '& .MuiChip-label': {
                          py: 0.75,
                          whiteSpace: 'normal',
                        },
                      }}
                    />
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {`${index + 1}. ${stripStepNumber(step.title)}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {step.summary}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                      {content.workflowOutputLabel}
                    </Typography>
                    <Stack spacing={1}>
                      {step.outputs.map((output) => (
                        <Stack
                          key={output}
                          direction="row"
                          spacing={1.25}
                          alignItems="flex-start"
                        >
                          <VerifiedOutlinedIcon
                            sx={{
                              mt: '2px',
                              fontSize: '18px',
                              color: theme.palette.main,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {output}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {!showSearchFallback && !visibleWorkflowSteps.length && (
              <Card
                sx={{
                  mt: 1,
                  borderRadius: '24px',
                  border: `1px dashed ${alpha(theme.palette.mainDark, 0.18)}`,
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {content.workflowEmptyTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {content.workflowEmptyDescription}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {!showSearchFallback && flowchartMatches && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h3" sx={{ mb: 1.5 }}>
              {content.flowchartTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {content.flowchartDescription}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  xl: 'minmax(0, 1.05fr) minmax(0, 1.2fr)',
                },
              }}
            >
              <Card
                sx={{
                  borderRadius: '28px',
                  border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Chip
                    size="small"
                    label={content.flowchartStepLabel}
                    sx={{
                      mb: 1.5,
                      bgcolor: alpha(theme.palette.mainDark, 0.08),
                      color: theme.palette.mainDark,
                    }}
                  />
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {content.flowchartExampleTitle}
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: 2 }}>
                    {content.flowchartSteps.map((step) => (
                      <Accordion
                        key={step.title}
                        disableGutters
                        elevation={0}
                        sx={{
                          borderRadius: '20px !important',
                          border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                          backgroundColor: alpha(theme.palette.mainLight, 0.05),
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box>
                            <Typography variant="h6" sx={{ mb: 0.5 }}>
                              {step.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {step.description}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={1.25}>
                            {step.bullets.map((bullet) => (
                              <Stack
                                key={bullet}
                                direction="row"
                                spacing={1.25}
                                alignItems="flex-start"
                              >
                                <VerifiedOutlinedIcon
                                  sx={{
                                    mt: '2px',
                                    fontSize: '18px',
                                    color: theme.palette.main,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {bullet}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1.25 }}>
                    {content.flowchartLegendLabel}
                  </Typography>
                  <Stack spacing={1}>
                    {content.flowchartLegendItems.map((item) => (
                      <Stack
                        key={item}
                        direction="row"
                        spacing={1.25}
                        alignItems="flex-start"
                      >
                        <VerifiedOutlinedIcon
                          sx={{
                            mt: '2px',
                            fontSize: '18px',
                            color: theme.palette.main,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {item}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>

              <Card
                sx={{
                  borderRadius: '28px',
                  border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                  boxShadow: 'none',
                  background: `
                    linear-gradient(180deg, ${alpha(theme.palette.mainLight, 0.08)} 0%, ${alpha(theme.palette.common.white, 0.92)} 100%)
                  `,
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 2.5 }}
                  >
                    <Box>
                      <Chip
                        size="small"
                        label={content.flowchartCanvasTitle}
                        sx={{
                          mb: 1.5,
                          bgcolor: alpha(theme.palette.mainDark, 0.08),
                          color: theme.palette.mainDark,
                        }}
                      />
                      <Typography variant="h4" sx={{ mb: 0.75 }}>
                        {content.flowchartCanvasTitle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {content.flowchartCanvasHint}
                      </Typography>
                    </Box>
                    <Chip
                      label={content.flowchartBoundaryLabel}
                      sx={{
                        alignSelf: { xs: 'flex-start', md: 'flex-start' },
                        bgcolor: alpha(theme.palette.main, 0.08),
                        color: theme.palette.mainDark,
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      borderRadius: '24px',
                      p: 2,
                      border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                      backgroundColor: alpha(theme.palette.common.white, 0.84),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', xl: 'row' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.25}
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <Box
                          sx={{
                            minWidth: 180,
                            px: 2,
                            py: 1.25,
                            borderRadius: '999px',
                            border: `1px solid ${alpha(
                              theme.palette.mainDark,
                              0.1
                            )}`,
                            backgroundColor: theme.palette.common.white,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {content.flowchartFilterByStageLabel}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            minWidth: 220,
                            px: 2,
                            py: 1.25,
                            borderRadius: '999px',
                            border: `1px solid ${alpha(
                              theme.palette.mainDark,
                              0.1
                            )}`,
                            backgroundColor: theme.palette.common.white,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {content.flowchartAreaLabel}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: 'wrap', alignItems: 'center' }}
                      >
                        {(
                          [
                            content.flowchartTabs.flowchart,
                            content.flowchartTabs.impact,
                            content.flowchartTabs.analytics,
                          ] as string[]
                        ).map((label, index) => (
                          <Box
                            key={label}
                            sx={{
                              px: 1.75,
                              py: 1,
                              borderRadius: '14px',
                              border: `1px solid ${alpha(
                                theme.palette.mainDark,
                                index === 0 ? 0.18 : 0.08
                              )}`,
                              backgroundColor:
                                index === 0
                                  ? alpha(theme.palette.main, 0.08)
                                  : theme.palette.common.white,
                              color:
                                index === 0
                                  ? theme.palette.mainDark
                                  : theme.palette.text.secondary,
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        overflowX: 'auto',
                        pb: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 1440 }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(7, minmax(180px, 1fr))',
                            gap: 1.5,
                            mb: 1,
                          }}
                        >
                          {content.flowchartExampleStages.map(
                            (stage, stageIndex) => {
                              let groupLabel = '';

                              if (stageIndex === 0) {
                                groupLabel = content.flowchartGroupCradleLabel;
                              } else if (
                                stageIndex ===
                                content.flowchartExampleStages.length - 1
                              ) {
                                groupLabel = content.flowchartGroupGraveLabel;
                              }

                              return (
                                <Typography
                                  key={`${stage.title}-group`}
                                  variant="caption"
                                  sx={{
                                    minHeight: 18,
                                    color: alpha(theme.palette.mainDark, 0.72),
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textAlign: 'center',
                                  }}
                                >
                                  {groupLabel}
                                </Typography>
                              );
                            }
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns:
                              'repeat(7, minmax(180px, 1fr))',
                            gap: 1.5,
                            alignItems: 'stretch',
                          }}
                        >
                          {content.flowchartExampleStages.map(
                            (stage, stageIndex) => {
                              const isCradle = stageIndex < 3;
                              const processItem = stage.items[0];
                              const objectItems = stage.items.slice(1);

                              return (
                                <Box
                                  key={stage.title}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      borderRadius: '20px',
                                      p: 1.25,
                                      border: `1px solid ${alpha(
                                        theme.palette.mainDark,
                                        0.08
                                      )}`,
                                      backgroundColor: alpha(
                                        isCradle
                                          ? theme.palette.mainLight
                                          : theme.palette.main,
                                        isCradle ? 0.05 : 0.04
                                      ),
                                      minHeight: 420,
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        minHeight: 56,
                                        mb: 1.25,
                                        color: theme.palette.mainDark,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.02em',
                                      }}
                                    >
                                      {stage.title}
                                    </Typography>

                                    <Box
                                      sx={{
                                        borderRadius: '14px',
                                        overflow: 'hidden',
                                        border: `1px solid ${alpha(
                                          theme.palette.mainDark,
                                          0.08
                                        )}`,
                                        backgroundColor:
                                          theme.palette.common.white,
                                        mb: 1,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          px: 1.25,
                                          py: 1,
                                          bgcolor: '#e0e0e0',
                                          color: '#4D4D4D',
                                          borderBottom: `1px solid ${alpha(
                                            theme.palette.mainDark,
                                            0.08
                                          )}`,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          sx={{
                                            textTransform: 'uppercase',
                                            textAlign: 'center',
                                          }}
                                        >
                                          {processItem}
                                        </Typography>
                                      </Box>
                                      <Stack
                                        sx={{ p: 1.25, flex: 1 }}
                                        spacing={0.9}
                                      >
                                        {objectItems.length ? (
                                          objectItems.map((item, itemIndex) => (
                                            <Box
                                              key={item}
                                              sx={{
                                                borderRadius: '12px',
                                                px: 1,
                                                py: 0.9,
                                                backgroundColor: alpha(
                                                  theme.palette.common.black,
                                                  0.015
                                                ),
                                                border: `1px solid ${alpha(
                                                  theme.palette.mainDark,
                                                  0.06
                                                )}`,
                                                minHeight: 62,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  mb: 0.5,
                                                  fontWeight: 600,
                                                  lineHeight: 1.25,
                                                }}
                                              >
                                                {item}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                {`${(itemIndex + 1) * (stageIndex + 1)}.${stageIndex + 2} kg CO2e`}
                                              </Typography>
                                            </Box>
                                          ))
                                        ) : (
                                          <Box
                                            sx={{
                                              borderRadius: '12px',
                                              px: 1,
                                              py: 0.9,
                                              backgroundColor: alpha(
                                                theme.palette.common.black,
                                                0.015
                                              ),
                                              border: `1px solid ${alpha(
                                                theme.palette.mainDark,
                                                0.06
                                              )}`,
                                              minHeight: 62,
                                              display: 'flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                            >
                                              {content.flowchartNoObjectsLabel}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Stack>
                                    </Box>

                                    <Box
                                      sx={{
                                        borderRadius: '12px',
                                        p: 1,
                                        textAlign: 'center',
                                        backgroundColor:
                                          theme.palette.common.white,
                                        border: `1px solid ${alpha(
                                          theme.palette.mainDark,
                                          0.06
                                        )}`,
                                        minHeight: 72,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {content.flowchartTabs.impact}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={700}
                                      >
                                        {`${flowchartImpactValues[stageIndex]} ${content.flowchartSummaryUnit}`}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            }
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mt: 1.5,
                        borderRadius: '20px',
                        border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                        backgroundColor: theme.palette.common.white,
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 0.5, color: theme.palette.mainDark }}
                      >
                        {content.flowchartSummaryLabel}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ color: theme.palette.mainDark, mb: 0.25 }}
                      >
                        {content.flowchartSummaryValue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {content.flowchartSummaryUnit}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}

        {(!showSearchFallback || visibleGuideSections.length > 0) && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h3" sx={{ mb: 1.5 }}>
              {content.guideTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {content.guideDescription}
            </Typography>

            <Stack spacing={2}>
              {visibleGuideSections.map((section) => (
                <Card
                  key={section.title}
                  sx={{
                    borderRadius: '28px',
                    border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                    boxShadow: 'none',
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Chip
                      size="small"
                      label={section.badge}
                      sx={{
                        mb: 1.5,
                        bgcolor: alpha(theme.palette.mainDark, 0.08),
                        color: theme.palette.mainDark,
                      }}
                    />
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {section.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {section.description}
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2.5,
                        gridTemplateColumns: section.visualId
                          ? {
                              xs: '1fr',
                              xl: 'minmax(0, 1.2fr) minmax(300px, 360px)',
                            }
                          : '1fr',
                        alignItems: 'start',
                      }}
                    >
                      <Stack spacing={1.25}>
                        {section.entries.map((entry) => (
                          <Accordion
                            key={entry.title}
                            disableGutters
                            elevation={0}
                            sx={{
                              borderRadius: '20px !important',
                              border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                              backgroundColor: alpha(
                                theme.palette.mainLight,
                                0.05
                              ),
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box>
                                <Typography variant="h6" sx={{ mb: 0.5 }}>
                                  {entry.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {entry.description}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Stack spacing={1.25}>
                                {entry.bullets.map((bullet) => (
                                  <Stack
                                    key={bullet}
                                    direction="row"
                                    spacing={1.25}
                                    alignItems="flex-start"
                                  >
                                    <VerifiedOutlinedIcon
                                      sx={{
                                        mt: '2px',
                                        fontSize: '18px',
                                        color: theme.palette.main,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {bullet}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </Stack>

                      {section.visualId === 'analytics-guide' && (
                        <AnalyticsGuideVisual language={i18n.language} />
                      )}
                      {section.visualId === 'passport-guide' && (
                        <PassportGuideVisual language={i18n.language} />
                      )}
                      {section.visualId === 'report-guide' && (
                        <ReportGuideVisual language={i18n.language} />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            {!showSearchFallback && !visibleGuideSections.length && (
              <Card
                sx={{
                  mt: 1,
                  borderRadius: '24px',
                  border: `1px dashed ${alpha(theme.palette.mainDark, 0.18)}`,
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    {content.guideEmptyTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {content.guideEmptyDescription}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        <Box sx={{ mt: 5 }}>
          <Card
            sx={{
              borderRadius: '28px',
              overflow: 'hidden',
              background: `
                linear-gradient(135deg, ${alpha(theme.palette.mainDark, 0.96)} 0%, ${theme.palette.mainDark} 42%, ${theme.palette.main} 100%)
              `,
              color: theme.palette.common.white,
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={2.5}>
                <Box>
                  <Chip
                    icon={<TaskAltOutlinedIcon />}
                    label={content.checklistLabel}
                    sx={{
                      mb: 1.5,
                      bgcolor: alpha(theme.palette.common.white, 0.12),
                      color: theme.palette.common.white,
                    }}
                  />
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {content.checklistTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.82 }}>
                    {readiness}% {content.checklistProgressLabel}
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={readiness}
                  sx={{
                    height: 10,
                    borderRadius: '999px',
                    bgcolor: alpha(theme.palette.common.white, 0.18),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: '999px',
                      bgcolor: '#f4d08c',
                    },
                  }}
                />

                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, minmax(0, 1fr))',
                    },
                  }}
                >
                  {content.checklistItems.map((item) => {
                    const completed = completedItems.includes(item.id);

                    return (
                      <Box
                        key={item.id}
                        onClick={() => toggleChecklistItem(item.id)}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.25,
                          p: 2,
                          borderRadius: '18px',
                          cursor: 'pointer',
                          bgcolor: alpha(
                            theme.palette.common.white,
                            completed ? 0.16 : 0.08
                          ),
                          border: `1px solid ${alpha(
                            theme.palette.common.white,
                            completed ? 0.22 : 0.12
                          )}`,
                          transition:
                            'transform 0.2s ease, background-color 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            bgcolor: alpha(theme.palette.common.white, 0.18),
                          },
                        }}
                      >
                        {completed ? (
                          <TaskAltOutlinedIcon sx={{ color: '#f4d08c' }} />
                        ) : (
                          <RadioButtonUncheckedOutlinedIcon
                            sx={{ opacity: 0.9 }}
                          />
                        )}
                        <Typography variant="body2">{item.label}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {(!showSearchFallback || visibleFaqs.length > 0) && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {content.faqTitle}
            </Typography>
            <Stack spacing={1.5}>
              {visibleFaqs.map((faq) => (
                <Accordion
                  key={faq.question}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: '20px !important',
                    border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>

            {!showSearchFallback && !visibleFaqs.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {content.noMatchingQuestionsLabel}
              </Typography>
            )}
          </Box>
        )}

        {!showSearchFallback && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {content.officialResourcesTitle}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, minmax(0, 1fr))',
                },
              }}
            >
              {content.officialResources.map((resource) => (
                <Card
                  key={resource.label}
                  sx={{
                    borderRadius: '24px',
                    border: `1px solid ${alpha(theme.palette.mainDark, 0.08)}`,
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      {resource.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {resource.summary}
                    </Typography>
                    <Button
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<LaunchOutlinedIcon />}
                    >
                      {content.officialResourceButtonLabel}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </LcaLayout>
  );
}
